import { type Emblem, sanitizeEmblem } from "./emblem";
import { isCollectibleSkin } from "./skinCollection";

/** A saved skin design: a base skin (uniform or a collected one) plus the drawn emblem. Stored per wallet address. */
export interface SkinDesign {
    name: string
    skin: string
    ops: Emblem | undefined
    savedAt: number
    /**
     * drawn over a base skin (a collected one), now or once (Customize's Load keeps the drawing as the base changes): never
     * sold on the flea market (0.99, the owner: "make emblems drawn over unsellable, and only those who are original
     * legibile"). Set by the editor as it's drawn, and by the server for one saved on a skin
     */
    drawnOver?: boolean
}

export const DESIGN_LIMITS = { maxDesigns: 24, maxNameLength: 24 } as const;

/** validates a design coming from a client; `defaultSkin` is the uniform id */
export function sanitizeDesign(raw: unknown, defaultSkin: string): SkinDesign | undefined {
    if (typeof raw !== "object" || raw === null) return undefined;
    const d = raw as { name?: unknown, skin?: unknown, ops?: unknown, savedAt?: unknown, drawnOver?: unknown };
    if (typeof d.name !== "string") return undefined;
    // control characters (line breaks, tabs...) are dropped: a name is one plain line
    const name = d.name.replace(/\p{Cc}/gu, "").trim().slice(0, DESIGN_LIMITS.maxNameLength);
    if (!name) return undefined;
    const skin = typeof d.skin === "string" && (d.skin === defaultSkin || isCollectibleSkin(d.skin)) ? d.skin : defaultSkin;
    // a time that isn't one (NaN, Infinity) isn't kept: it was saved as null in the player data file
    const savedAt = typeof d.savedAt === "number" && Number.isFinite(d.savedAt) ? d.savedAt : Date.now();
    // (on a skin, or said so: drawn over one, whatever its base is now; 0.99)
    const drawnOver = d.drawnOver === true || (typeof d.skin === "string" && d.skin !== defaultSkin);
    return { name, skin, ops: sanitizeEmblem(d.ops), savedAt, ...(drawnOver ? { drawnOver: true } : {}) };
}
